import { useMemo } from 'react'

import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import type {
  ExemptionsApiGetExemptionsRequest,
  TaxExemptionModel,
  TaxExemptionOutput,
} from '@b-stock/location-api-client'

import exemptionApi from '@api/exemption'
import useUserInfo from '@helpers/useUserInfo'

const baseKey = 'exemptions'

export const useExemptions = (
  filters?: ExemptionsApiGetExemptionsRequest,
  opts?: UseQueryOptions<unknown, unknown, TaxExemptionOutput | undefined, any>
) => {
  const { accountId } = useUserInfo()
  const queryKey = [baseKey, accountId, filters]

  const query = useQuery({
    queryKey: queryKey,

    queryFn: async () => {
      const allExemptions: TaxExemptionOutput['exemptions'] = []
      const pageLimit = 100

      let total = 0
      let tempOffset = 0
      let lastResp = null

      do {
        const resp = await exemptionApi.getTaxExemptions({
          offset: tempOffset,
          limit: pageLimit,
          ...filters,
        })
        lastResp = resp

        allExemptions.push(...resp.exemptions)
        total = resp.total
        tempOffset += pageLimit
      } while (tempOffset < total)

      return {
        ...lastResp,
        exemptions: allExemptions,
      }
    },

    refetchOnMount: 'always',
    ...opts,
    enabled: !!accountId,
    placeholderData: { total: 0, limit: 0, offset: 0, exemptions: [] },
  })

  return {
    ...query,
    isLoading: query.isLoading || query.isFetching,
  }
}

type UseExemptionByJurisdictionArg = {
  country?: string
  region?: string
}

export const useExemptionByJurisdiction = (
  params: UseExemptionByJurisdictionArg,
  opts?: Omit<
    UseQueryOptions<unknown, unknown, TaxExemptionModel | undefined, any>,
    'queryKey' | 'queryFn'
  >
) => {
  const { accountId } = useUserInfo()

  const queryKey = useMemo(
    () => ['exemptionByJurisdiction', accountId, params],
    [accountId, params]
  )

  const query = useQuery({
    queryKey: queryKey,

    queryFn: () => {
      if (!params.country || !accountId) {
        return null
      }

      return exemptionApi.getTaxExemptionByJurisdiction({
        accountId,
        country: params.country,
        region: params.region,
      })
    },
    ...opts,
  })
  return query
}
